/** Judge screen — compact scoring with decimal sub-grid and attempt tracking. */

import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/api/client";
import { RaleySocket } from "@/api/ws";
import { useCompetitionStore } from "@/stores/competition";
import { useAuthStore } from "@/stores/auth";
import { useNotifications, NotificationToasts } from "@/hooks/useNotifications";
import type { HeatRider, LiveState, Member, Score, WSMessage } from "@/types";

const WHOLE_SCORES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export function JudgePage() {
  const { competitionId } = useParams<{ competitionId: string }>();
  const user = useAuthStore((s) => s.user);
  const { competition, heats, divisions, liveState, loadCompetition, loadHeats, loadDivisions, loadLiveState, setLiveState } = useCompetitionStore();
  const [selectedHeat, setSelectedHeat] = useState<string | null>(null);
  const [heatRiders, setHeatRiders] = useState<HeatRider[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  // scores keyed by `${riderId}:${attemptNumber}` -> value
  const [myScores, setMyScores] = useState<Record<string, number>>({});
  // which whole number is expanded for the decimal sub-grid, keyed by riderId
  const [expandedScore, setExpandedScore] = useState<Record<string, number | null>>({});
  // which attempt the judge is currently viewing/scoring per rider
  const [judgeAttempt, setJudgeAttempt] = useState<Record<string, number>>({});
  // For multi-attempt: which riders have the score picker open (vs attempt boxes)
  const [scoringModeRiders, setScoringModeRiders] = useState<Record<string, boolean>>({});
  // Incremented to trigger re-fetch of riders/scores (e.g., on WebSocket scores_update)
  const [refreshTick, setRefreshTick] = useState(0);
  // Shared notifications
  const { items: notifications, notify } = useNotifications();
  const prevLiveHeatRef = useRef<string | null>(null);
  // Track previous scores for diffing (to notify on other judges' scores)
  const prevScoresRef = useRef<Map<string, number>>(new Map());
  // Admin impersonation: which judge slot are we scoring as?
  const [scoringAsJudgeNumber, setScoringAsJudgeNumber] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(true);

  // Refs for values used in WebSocket handler — avoids reconnecting on every state change
  const myMembershipRef = useRef<Member | undefined>(undefined);
  useEffect(() => { myMembershipRef.current = members.find((m) => m.user_id === user?.id); }, [members, user?.id]);
  const membersRef = useRef(members); membersRef.current = members;
  const heatRidersRef = useRef(heatRiders); heatRidersRef.current = heatRiders;
  const scoringAsRef = useRef(scoringAsJudgeNumber); scoringAsRef.current = scoringAsJudgeNumber;
  const selectedHeatRef = useRef(selectedHeat); selectedHeatRef.current = selectedHeat;
  const notifyRef = useRef(notify); notifyRef.current = notify;

  useEffect(() => {
    if (!competitionId) return;
    Promise.all([
      loadCompetition(competitionId),
      loadHeats(competitionId),
      loadDivisions(competitionId),
      loadLiveState(competitionId),
      api.get<Member[]>(`/api/competitions/${competitionId}/members`).then(setMembers),
    ]).then(() => setLoading(false));
  }, [competitionId, loadCompetition, loadHeats, loadDivisions, loadLiveState]);

  // WebSocket for real-time live state + attempt updates
  useEffect(() => {
    if (!competition) return;
    const wsUrl = `${import.meta.env.VITE_WS_URL ?? `ws://${window.location.host}`}/api/obs/${competition.obs_token}/ws`;
    const socket = new RaleySocket(wsUrl);
    socket.connect();

    const unsubStatus = socket.onStatus(setWsConnected);

    const unsub = socket.subscribe((msg: WSMessage) => {
      if (msg.type === "live_state") {
        const newState = msg as unknown as LiveState;
        const oldHeatId = prevLiveHeatRef.current;
        setLiveState(newState);

        if (newState.is_live && newState.live_heat_id && newState.live_heat_id !== oldHeatId) {
          notifyRef.current({ text: "Live heat changed — tap 'Switch to live' to follow", color: "#eab308", ding: true });
        } else if (!newState.is_live && oldHeatId) {
          notifyRef.current({ text: "Broadcast went dark", color: "#71717a" });
        }
        prevLiveHeatRef.current = newState.is_live ? newState.live_heat_id : null;
      }
      if (msg.type === "attempt_update" || msg.type === "rider_attempt_update") {
        setHeatRiders((prev) => {
          const rider = prev.find((hr) => hr.rider.id === msg.rider_id);
          if (rider && msg.current_attempt > rider.current_attempt) {
            notifyRef.current({ text: `${rider.rider.name} — attempt ${msg.current_attempt} now available`, color: "#22c55e", ding: true });
          }
          return prev.map((hr) =>
            hr.rider.id === msg.rider_id
              ? { ...hr, current_attempt: msg.current_attempt }
              : hr,
          );
        });
      }
      if (msg.type === "scores_update") {
        const myJudgeNum = scoringAsRef.current ?? myMembershipRef.current?.judge_number;
        const heat = selectedHeatRef.current;
        if (competitionId && heat) {
          api.get<Score[]>(`/api/competitions/${competitionId}/heats/${heat}/scores`).then((newScores) => {
            const prev = prevScoresRef.current;
            let dinged = false;
            for (const s of newScores) {
              const key = `${s.rider_id}:${s.judge_number}:${s.attempt_number}`;
              if (prev.get(key) !== s.value && s.judge_number !== myJudgeNum) {
                const judge = membersRef.current.find((m) => m.judge_number === s.judge_number);
                const judgeName = judge?.judge_name ?? judge?.user?.display_name?.split(" ")[0] ?? `Judge ${s.judge_number}`;
                const rider = heatRidersRef.current.find((hr) => hr.rider.id === s.rider_id);
                const riderName = rider?.rider.name ?? "Rider";
                notifyRef.current({
                  text: `scored ${riderName} #${s.attempt_number}`,
                  judgeName,
                  judgeNum: s.judge_number,
                  detail: s.value.toFixed(1),
                  ding: !dinged,
                });
                dinged = true;
              }
            }
            const newMap = new Map<string, number>();
            for (const s of newScores) {
              newMap.set(`${s.rider_id}:${s.judge_number}:${s.attempt_number}`, s.value);
            }
            prevScoresRef.current = newMap;
          });
        }
        setRefreshTick((n) => n + 1);
      }
    });

    return () => { unsub(); unsubStatus(); socket.disconnect(); };
  }, [competition, competitionId, setLiveState]);

  // Periodically refresh heats list to pick up new heats/groups/riders added from Setup
  useEffect(() => {
    if (!competitionId) return;
    const interval = setInterval(() => {
      loadHeats(competitionId);
      loadDivisions(competitionId);
    }, 30_000);
    return () => clearInterval(interval);
  }, [competitionId, loadHeats, loadDivisions]);

  useEffect(() => {
    if (selectedHeat || heats.length === 0 || loading) return;
    setSelectedHeat(liveState?.live_heat_id ?? heats[0].id);
  }, [heats, selectedHeat, liveState?.live_heat_id, loading]);

  const isInitialLoad = useRef(true);
  useEffect(() => {
    if (!competitionId || !selectedHeat) return;
    const isFirst = isInitialLoad.current;
    Promise.all([
      api.get<HeatRider[]>(`/api/competitions/${competitionId}/heats/${selectedHeat}/riders`),
      api.get<Score[]>(`/api/competitions/${competitionId}/heats/${selectedHeat}/scores`),
    ]).then(([riders, scores]) => {
      setHeatRiders(riders);
      const effectiveJudgeNumber = scoringAsJudgeNumber ?? myMembershipRef.current?.judge_number;
      const mine: Record<string, number> = {};
      for (const s of scores) {
        if (effectiveJudgeNumber != null && s.judge_number === effectiveJudgeNumber) {
          mine[`${s.rider_id}:${s.attempt_number}`] = s.value;
        }
      }
      setMyScores(mine);

      // Only reset judge attempt view on initial load or heat change — not on background refresh
      if (isFirst || isInitialLoad.current) {
        const attempts: Record<string, number> = {};
        for (const r of riders) {
          const riderId = r.rider.id;
          let startAttempt = 1;
          for (let a = 1; a <= r.current_attempt; a++) {
            if (mine[`${riderId}:${a}`] == null) {
              startAttempt = a;
              break;
            }
            startAttempt = a + 1;
          }
          attempts[riderId] = Math.min(startAttempt, Math.max(r.current_attempt, 1));
        }
        setJudgeAttempt(attempts);
        setExpandedScore({});
        setScoringModeRiders({});
        isInitialLoad.current = false;
      }
    });
    // Reset initial flag when heat changes
    return () => { isInitialLoad.current = true; };
  }, [competitionId, selectedHeat, scoringAsJudgeNumber, refreshTick]);

  const setScore = useCallback(
    async (riderId: string, value: number, attemptNumber: number) => {
      if (!competitionId || !selectedHeat) return;
      await api.put(`/api/competitions/${competitionId}/heats/${selectedHeat}/scores/${riderId}`, {
        value,
        attempt_number: attemptNumber,
        ...(scoringAsJudgeNumber != null ? { judge_number: scoringAsJudgeNumber } : {}),
      });
      setMyScores((prev) => ({ ...prev, [`${riderId}:${attemptNumber}`]: value }));
      setExpandedScore((prev) => ({ ...prev, [riderId]: null }));

      // Auto-advance to next attempt and go back to attempt view
      const maxAttempts = heats.find((h) => h.id === selectedHeat)?.max_attempts ?? 1;
      if (maxAttempts > 1) {
        // Return to attempt boxes view
        setScoringModeRiders((prev) => ({ ...prev, [riderId]: false }));
        if (attemptNumber < maxAttempts) {
          setJudgeAttempt((prev) => ({ ...prev, [riderId]: attemptNumber + 1 }));
        }
      }
    },
    [competitionId, selectedHeat, scoringAsJudgeNumber, heats],
  );



  if (loading || !competition) {
    return <div className="loading-screen"><div className="loading-spinner" /></div>;
  }

  // Group heats by division
  const divisionMap = new Map<string | null, typeof heats>();
  for (const heat of heats) {
    const key = heat.division_id;
    if (!divisionMap.has(key)) divisionMap.set(key, []);
    divisionMap.get(key)!.push(heat);
  }

  const getDivisionName = (divisionId: string | null): string | null => {
    if (!divisionId) return null;
    return divisions.find((d) => d.id === divisionId)?.name ?? null;
  };

  const selectedHeatObj = heats.find((h) => h.id === selectedHeat);
  const maxAttemptsForHeat = selectedHeatObj?.max_attempts ?? 1;

  // Check if user is admin (owner counts as admin)
  const myMembership = members.find((m) => m.user_id === user?.id);
  const isAdmin = myMembership?.is_admin || competition.owner_id === user?.id;
  const isJudge = myMembership?.is_judge;
  const judgeMembers = members.filter((m) => m.is_judge);

  // Build judge slot labels for impersonation dropdown
  const getJudgeLabel = (m: Member): string => {
    const name = m.user.display_name?.split(" ")[0] ?? m.user.email;
    return m.judge_number
      ? `Judge ${m.judge_number} — ${name}`
      : name;
  };

  // Unassigned judge slots
  const assignedNumbers = new Set(judgeMembers.map((m) => m.judge_number).filter(Boolean));
  const unassignedSlots: number[] = [];
  for (let i = 1; i <= competition.judge_count; i++) {
    if (!assignedNumbers.has(i)) unassignedSlots.push(i);
  }

  // What we're currently scoring as
  const scoringAsMember = scoringAsJudgeNumber != null
    ? members.find((m) => m.judge_number === scoringAsJudgeNumber)
    : null;
  const scoringAsLabel = scoringAsJudgeNumber != null
    ? (scoringAsMember ? getJudgeLabel(scoringAsMember) : `Judge ${scoringAsJudgeNumber}`)
    : isAdmin && !isJudge ? "Select a judge" : null;

  // Display name: first name only
  const firstName = user?.display_name?.split(" ")[0] ?? "Judge";

  // Admin who is not a judge must impersonate — auto-select first judge if none selected
  const adminOnly = isAdmin && !isJudge;

  return (
    <div>
      {!wsConnected && (
        <div
          style={{
            background: "rgba(239, 68, 68, 0.15)",
            border: "1px solid #ef4444",
            borderRadius: 6,
            padding: "10px 16px",
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span style={{ fontSize: 13, color: "#ef4444", fontWeight: 600 }}>
            Connection lost — reload to restore live updates
          </span>
          <button
            className="btn btn-sm"
            style={{ background: "#ef4444", color: "#fff", border: "none", fontSize: 11, whiteSpace: "nowrap" }}
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      )}
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Judge: {firstName}</h1>
          <p className="page-subtitle">
            {competition.name} · {competition.scoring_model} scoring
            {scoringAsLabel && !adminOnly && <> · Scoring as <strong>{scoringAsLabel}</strong></>}
            {adminOnly && scoringAsJudgeNumber != null && <> · Impersonating <strong>{scoringAsLabel}</strong></>}
            {adminOnly && scoringAsJudgeNumber == null && <> · <span style={{ color: "var(--c-warning)" }}>Select a judge to impersonate</span></>}
          </p>
        </div>
        {isAdmin && (
          <div>
            <label className="field-label" style={{ fontSize: 10, marginBottom: 6, display: "block" }}>
              {adminOnly ? "Impersonate" : "Score as"}
            </label>
            <select
              value={scoringAsJudgeNumber != null ? String(scoringAsJudgeNumber) : ""}
              onChange={(e) => setScoringAsJudgeNumber(e.target.value ? Number(e.target.value) : null)}
              style={{
                padding: "6px 10px",
                background: "var(--c-surface-2)",
                border: `1px solid ${adminOnly && scoringAsJudgeNumber == null ? "var(--c-warning)" : "var(--c-border)"}`,
                color: "var(--c-text)",
                fontSize: 12,
                minWidth: 220,
              }}
            >
              {adminOnly ? (
                <option value="">— Select judge —</option>
              ) : (
                <option value="">Myself (Judge {myMembership?.judge_number ?? ""})</option>
              )}
              {judgeMembers.filter((m) => m.judge_number != null).map((m) => (
                <option key={m.judge_number} value={String(m.judge_number)}>
                  {getJudgeLabel(m)}
                </option>
              ))}
              {unassignedSlots.map((n) => (
                <option key={`slot-${n}`} value={String(n)}>
                  Judge {n} — not assigned
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Live heat indicator */}
      {liveState?.is_live && liveState.live_heat_id && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 14px",
            marginBottom: 16,
            background: "var(--c-surface)",
            border: "1px solid var(--c-border)",
          }}
        >
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ef4444", boxShadow: "0 0 6px #ef4444", flexShrink: 0 }} />
          <span className="text-sm" style={{ fontWeight: 600 }}>
            Live Heat:{" "}
            <span style={{ color: "var(--c-accent)" }}>
              {(() => {
                const liveHeat = heats.find((h) => h.id === liveState.live_heat_id);
                if (!liveHeat) return "Unknown";
                const div = liveHeat.division_id ? divisions.find((d: { id: string }) => d.id === liveHeat.division_id) : null;
                return div ? `${div.name} — ${liveHeat.name}` : liveHeat.name;
              })()}
            </span>
          </span>
          {selectedHeat !== liveState.live_heat_id && (
            <button
              className="btn btn-sm btn-secondary"
              style={{ marginLeft: "auto", fontSize: 10 }}
              onClick={() => setSelectedHeat(liveState.live_heat_id)}
            >
              Switch to live
            </button>
          )}
        </div>
      )}

      {/* Block scoring if admin hasn't selected a judge */}
      {adminOnly && scoringAsJudgeNumber == null && (
        <div className="card" style={{ textAlign: "center", padding: 32, marginBottom: 24, border: "1px solid var(--c-warning)" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--c-warning)", marginBottom: 8 }}>Select a judge to impersonate</div>
          <div className="text-sm text-muted">As an admin, you score on behalf of judges. Pick a judge from the dropdown above.</div>
        </div>
      )}

      {/* Heat tabs grouped by division */}
      <div style={{ marginBottom: 16 }}>
        {Array.from(divisionMap.entries()).map(([divisionId, divisionHeats]) => {
          const divisionName = getDivisionName(divisionId);
          return (
            <div key={divisionId ?? "__none"} style={{ marginBottom: 8 }}>
              {divisionName && (
                <div
                  className="text-sm text-muted"
                  style={{
                    fontSize: 10,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: 4,
                    fontWeight: 600,
                  }}
                >
                  {divisionName}
                </div>
              )}
              <div className="flex gap-2" style={{ flexWrap: "wrap" }}>
                {divisionHeats.map((heat) => (
                  <button
                    key={heat.id}
                    className={`btn ${selectedHeat === heat.id ? "btn-primary" : "btn-secondary"} btn-sm`}
                    onClick={() => setSelectedHeat(heat.id)}
                  >
                    {heat.name}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Compact rider grid — blocked if admin hasn't picked a judge */}
      {adminOnly && scoringAsJudgeNumber == null ? null : heatRiders.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">No riders in this heat</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {/* Column header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "160px 1fr 40px",
              gap: 8,
              padding: "0 8px",
              alignItems: "center",
            }}
          >
            <span className="field-label">Rider</span>
            <span className="field-label">{maxAttemptsForHeat > 1 ? "Attempts / Score" : "Score"}</span>
            <span />
          </div>

          {heatRiders.map((hr) => {
            const riderId = hr.rider.id;
            const maxAttempts = selectedHeatObj?.max_attempts ?? 1;
            const currentAttempt = hr.current_attempt;
            const viewingAttempt = judgeAttempt[riderId] ?? 1;
            const scoreKey = `${riderId}:${viewingAttempt}`;
            const currentScore = myScores[scoreKey];
            const expanded = expandedScore[riderId] ?? null;

            // For multi-attempt: show attempt picker by default, score picker when attempt selected
            // scoringMode = true means we're showing the score picker for this rider
            const scoringMode = maxAttempts === 1 || (scoringModeRiders[riderId] ?? false);

            // Determine if all available attempts are scored
            let allScored = true;
            for (let a = 1; a <= currentAttempt; a++) {
              if (myScores[`${riderId}:${a}`] == null) {
                allScored = false;
                break;
              }
            }
            const waitingForAttempt =
              allScored && viewingAttempt > currentAttempt && currentAttempt < maxAttempts;

            return (
              <div
                key={hr.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "160px 1fr 40px",
                  gap: 8,
                  padding: "6px 8px",
                  background: currentScore != null ? "var(--c-surface)" : "transparent",
                  border: "1px solid var(--c-border)",
                  alignItems: "center",
                }}
              >
                {/* Name */}
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>
                    {hr.rider.name}
                  </div>
                  {hr.rider.nickname && (
                    <div className="text-muted" style={{ fontSize: 10 }}>
                      {hr.rider.nickname}
                    </div>
                  )}
                </div>

                {/* Score area — either attempt boxes OR score picker */}
                <div style={{ display: "flex", gap: 2, flexWrap: "nowrap", overflow: "hidden" }}>
                  {!scoringMode ? (
                    /* ── Attempt boxes (multi-attempt default view) ── */
                    <>
                      {Array.from({ length: maxAttempts }, (_, i) => {
                        const attemptNum = i + 1;
                        const attemptScore = myScores[`${riderId}:${attemptNum}`];
                        const hasScore = attemptScore != null;
                        const isLive = attemptNum === currentAttempt;
                        const isAvailable = attemptNum <= currentAttempt;
                        const isFuture = attemptNum > currentAttempt;
                        const needsScore = isAvailable && !hasScore;

                        return (
                          <button
                            key={attemptNum}
                            onClick={() => {
                              if (isFuture) return;
                              setJudgeAttempt((prev) => ({ ...prev, [riderId]: attemptNum }));
                              setExpandedScore((prev) => ({ ...prev, [riderId]: null }));
                              setScoringModeRiders((prev) => ({ ...prev, [riderId]: true }));
                            }}
                            disabled={isFuture}
                            style={{
                              flex: "1 1 0",
                              minWidth: 0,
                              height: 32,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 4,
                              fontSize: hasScore ? 11 : 9,
                              fontWeight: hasScore ? 700 : 500,
                              fontFamily: "var(--font-mono)",
                              background: hasScore
                                ? "var(--c-surface-2)"
                                : needsScore ? "rgba(234,179,8,0.1)" : "transparent",
                              color: hasScore
                                ? "var(--c-text)"
                                : needsScore ? "var(--c-warning)" : isFuture ? "var(--c-text-dim)" : "var(--c-text-muted)",
                              border: `1px solid ${needsScore ? "var(--c-warning)" : isLive ? "var(--c-success)" : "var(--c-border)"}`,
                              boxShadow: isLive && !needsScore ? "0 0 0 1px var(--c-success)" : "none",
                              cursor: isFuture ? "default" : "pointer",
                              opacity: isFuture ? 0.3 : 1,
                              transition: "all 0.1s",
                              padding: 0,
                            }}
                            title={`Attempt ${attemptNum}${hasScore ? ` — ${attemptScore.toFixed(1)}` : needsScore ? " (needs score)" : isFuture ? " (not available)" : ""}`}
                          >
                            {hasScore ? attemptScore.toFixed(1) : `#${attemptNum}`}
                            {needsScore && (
                              <div style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--c-warning)", flexShrink: 0 }} />
                            )}
                            {isLive && hasScore && (
                              <div style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--c-success)", flexShrink: 0 }} />
                            )}
                          </button>
                        );
                      })}
                      {waitingForAttempt && (
                        <div style={{
                          flex: "1 1 0",
                          minWidth: 0,
                          height: 32,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 9,
                          color: "var(--c-text-dim)",
                          fontStyle: "italic",
                          background: "var(--c-surface)",
                          border: "1px dashed var(--c-border)",
                        }}>
                          Waiting for next attempt
                        </div>
                      )}
                    </>
                  ) : expanded != null ? (
                    /* ── Decimal sub-grid: X.0 through X.9 ── */
                    <div style={{ display: "flex", gap: 2, width: "100%" }}>
                      <button
                        onClick={() => setExpandedScore((prev) => ({ ...prev, [riderId]: null }))}
                        style={{ flex: "0 0 24px", height: 32, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 600, background: "var(--c-surface-2)", color: "var(--c-text-muted)", border: "1px solid var(--c-border)", cursor: "pointer" }}
                        title="Back to whole numbers"
                      >
                        &larr;
                      </button>
                      {Array.from({ length: 10 }, (_, i) => {
                        const val = expanded + i * 0.1;
                        const rounded = Math.round(val * 10) / 10;
                        return (
                          <button
                            key={rounded}
                            onClick={() => setScore(riderId, rounded, viewingAttempt)}
                            style={{
                              flex: "1 1 0", minWidth: 0, height: 32,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 10, fontWeight: currentScore === rounded ? 700 : 500,
                              fontFamily: "var(--font-mono)",
                              background: currentScore === rounded ? "var(--c-accent)" : "var(--c-surface-2)",
                              color: currentScore === rounded ? "#fff" : "var(--c-text-muted)",
                              border: "1px solid transparent", cursor: "pointer",
                            }}
                          >
                            {rounded.toFixed(1)}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    /* ── Whole-number score buttons ── */
                    <>
                      {maxAttempts > 1 && (
                        <button
                          onClick={() => {
                            setScoringModeRiders((prev) => ({ ...prev, [riderId]: false }));
                            setExpandedScore((prev) => ({ ...prev, [riderId]: null }));
                          }}
                          style={{ flex: "0 0 24px", height: 32, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 600, background: "var(--c-surface-2)", color: "var(--c-text-muted)", border: "1px solid var(--c-border)", cursor: "pointer" }}
                          title="Back to attempts"
                        >
                          &larr;
                        </button>
                      )}
                      {WHOLE_SCORES.map((v) => {
                        const scored = currentScore != null && Math.floor(currentScore) === v;
                        const hasDecimal = scored && currentScore !== v && v !== 10;
                        return (
                          <button
                            key={v}
                            onClick={() => {
                              if (v === 10) {
                                setScore(riderId, 10.0, viewingAttempt);
                              } else {
                                setExpandedScore((prev) => ({ ...prev, [riderId]: v }));
                              }
                            }}
                            style={{
                              flex: "1 1 0", minWidth: 0, height: 32,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: hasDecimal ? 10 : 11, fontWeight: scored ? 700 : 500,
                              fontFamily: "var(--font-mono)",
                              background: scored ? "var(--c-accent)" : "var(--c-surface-2)",
                              color: scored ? "#fff" : "var(--c-text-muted)",
                              border: "1px solid transparent", cursor: "pointer",
                            }}
                          >
                            {hasDecimal ? (currentScore ?? 0).toFixed(1) : v}
                          </button>
                        );
                      })}
                    </>
                  )}
                </div>

                {/* Status indicator */}
                <div style={{ fontSize: 9, color: "var(--c-text-dim)", textAlign: "center", lineHeight: 1.2 }}>
                  {waitingForAttempt && !scoringMode ? "Wait" : ""}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <NotificationToasts items={notifications} />
    </div>
  );
}

